import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut } from "@repo/blacksmith-ui/command";
import { CommandDialogContent } from "@repo/blacksmith-ui/command-dialog";
import { Workflow, CirclePlay, Terminal, GaugeCircleIcon, DatabaseZap, HardDrive, BarChart3, Settings, Search, FileText, HelpCircle, GitPullRequest, GitMerge, GitPullRequestClosed, GitPullRequestDraft, Building2, Sun, Moon, Monitor, Sparkles, Clock, Activity, MessageSquare, FolderKanban, Zap, Layers, Ruler, Archive, ArchiveRestore } from "lucide-react";
import { SiDocker } from "@icons-pack/react-simple-icons";
import { useTheme } from "next-themes";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { canUsePerfDebug, setPerfDebugEnabled, usePerfDebugEnabled } from "@/lib/perfDebugMode";
import { formatDistanceToNow } from "date-fns";
import { useWorkflowCache } from "@/hooks/useWorkflowCache";
import { useOrgFromPathname } from "@/hooks/useOrgFromPathname";
import { useActiveGithubOrgForUserNonSuspense, useIsImpersonatingNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useAssistantSessionCache, getRecentSessions, type SearchedSession } from "@/hooks/useAssistantSessionCache";
import { useAssistantSessionSearch } from "@/hooks/useAssistantSessionSearch";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useAuth } from "@/components/AuthProvider";
import { useIsMac } from "@/hooks/useIsMac";
import { Badge } from "@repo/blacksmith-ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useAtom, useAtomValue } from "jotai";
import { recentTimeTabAtom, recentDateRangeAtom } from "@/atoms/runsFilterAtoms";
import { selectedCodesmithSessionIdAtom } from "@/atoms/codesmithSessionAtoms";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { GithubActionsIcon } from "@/components/icons/GithubActionsIcon";
import { labelsToDateRanges } from "@/lib/utils";
import { useDateRangeQueryParams } from "@/hooks/useDateRangeQueryParams";
import { navigationItemMatchesSearch, getNavigationItemPriority as getNavigationItemPriorityFor, getActionItemPriority as getActionItemPriorityFor } from "@/components/commandPaletteMatch";
import { CODESMITH_CONVERSATION_ACTION_EVENT, codesmithUsagePath, currentCodesmithConversationAction, githubActionsUsagePath, settingsShortcutPath } from "@/components/commandPaletteShortcuts";
import { isCodesmithPath, settingsPagePath } from "@/components/sidebar/useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "@/components/sidebar/useCodesmithEnabledCache";
import { settingsPath } from "@/lib/settingsRoutes";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import type { AssistantSession } from "@/components/codesmith/types";
import { useQuery, useQueryClient } from "react-query";
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const router = useRouter();
  // Use pathname-based org to avoid suspense from useActiveGithubOrgForUser
  const urlOrg = useOrgFromPathname();
  const currentOrg = router.query.org as string || urlOrg;
  // The desktop app lists only [code]smith commands; settings there always
  // means [code]smith settings.
  const desktop = useCodesmithSurface(router.query.embed) === "desktop";
  const {
    workflows,
    jobs
  } = useWorkflowCache({
    enabled: !desktop
  });
  const {
    codesmithAutomationsEnabled,
    activeGithubOrg,
    activeGithubOrgBlocked,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  // Cache-backed like the rail's Codesmith row: while the org query is in
  // flight (or errored) this assumes the last resolved value, so the usage
  // command is reachable before /user/github/orgs answers. Only a resolved
  // or cached false hides it.
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  // Prefer the URL/pathname org, but fall back to the active org resolved by
  // the connected-orgs hook (covers legacy routes like /logs that have no
  // org segment, where currentOrg would otherwise be empty/null). While orgs
  // are unresolved the cached last-active org backstops it, so navigation
  // targets use the same org the cached codesmithEnabled was read from.
  const resolvedOrg = currentOrg || activeGithubOrg || (orgsResolved ? undefined : getCachedLastActiveOrg());
  const {
    sessions: cachedSessions
  } = useAssistantSessionCache();
  const {
    user
  } = useAuth();
  const {
    sessions: matchingSessions,
    isLoading: isSessionSearchLoading
  } = useAssistantSessionSearch(search, user?.id, open);
  const commandRef = useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = useState("");
  const hasExplicitSelectionRef = useRef(false);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) setSelectedValue("");
  }
  useEffect(() => {
    if (!open) hasExplicitSelectionRef.current = false;
  }, [open]);

  // cmdk only auto-selects the first item when the search changes, so session
  // matches that arrive later would land above a stale highlight. Move the
  // default selection to the new first row unless the user picked one.
  useEffect(() => {
    if (hasExplicitSelectionRef.current) return;
    const firstItem = commandRef.current?.querySelector('[cmdk-item]:not([aria-disabled="true"])');
    const firstValue = firstItem?.getAttribute("data-value");
    if (firstValue) setSelectedValue(firstValue);
  }, [matchingSessions]);
  const isImpersonating = useIsImpersonatingNonSuspense();
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const perfDebugEnabled = usePerfDebugEnabled();
  const perfDebugActionName = perfDebugEnabled ? "Hide FPS monitor" : "Show FPS monitor (frame rate debug)";
  const [, setRecentTimeTab] = useAtom(recentTimeTabAtom);
  const [, setRecentDateRange] = useAtom(recentDateRangeAtom);
  const {
    updateDateRangeUrl
  } = useDateRangeQueryParams({
    source: "recent_range"
  });
  const isMac = useIsMac();
  const isOnCodesmithPage = desktop || isCodesmithPath(router.pathname);
  const isOnSessionPage = router.pathname.includes("/sessions") || router.pathname.includes("/assistant");
  const currentSessionId = useAtomValue(selectedCodesmithSessionIdAtom);
  const cachedCurrentSession = currentSessionId ? queryClient.getQueryData<AssistantSession>(["assistantSession", currentSessionId]) : undefined;
  const {
    data: currentSession
  } = useQuery<AssistantSession | undefined>(["assistantSession", currentSessionId], () => cachedCurrentSession, {
    enabled: false,
    initialData: cachedCurrentSession
  });
  const conversationAction = isOnSessionPage && !isImpersonating && !activeGithubOrgBlocked ? currentCodesmithConversationAction({
    sessionId: currentSessionId,
    session: currentSession,
    userId: user?.id
  }) : null;
  const navigate = useCallback((path: string) => {
    router.push(path);
    setOpen(false);
    setSearch("");
  }, [router]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Open command palette on cmd+k or ctrl+k (always, even inside inputs)
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen(prev => !prev);
        return;
      }
      const settingsPath = settingsShortcutPath(event, resolvedOrg, isOnCodesmithPage);
      if (settingsPath) {
        event.preventDefault();
        // navigate (not a bare router.push) so an already-open palette closes
        // instead of overlaying the settings page.
        navigate(settingsPath);
        return;
      }

      // Don't trigger other shortcuts if user is typing in an input/textarea (except for Escape key)
      if (event.key !== "Escape" && event.target instanceof HTMLElement && (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA" || event.target.isContentEditable)) {
        return;
      }
      if (event.key === "Escape" && open) {
        event.preventDefault();
        if (search) {
          setSearch("");
        } else {
          setOpen(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOnCodesmithPage, navigate, open, resolvedOrg, search]);
  const truncate = (text: string, length: number) => {
    if (text.length <= length) return text;
    return text.substring(0, length) + "...";
  };

  // Shared renderer for a Codesmith session row (used by both search
  // results and the empty-search "Recent Sessions" group)
  const renderSessionItem = (session: SearchedSession) => <CommandItem onSelect={() => {
    const url = currentOrg ? `/${currentOrg}/sessions/${session.id}` : `/sessions/${session.id}`;
    navigate(url);
  }} className="flex items-center justify-between cursor-pointer">
      <div className="flex items-center min-w-0">
        {session.mode === "project_coordinator" ? <FolderKanban className="mr-2 h-4 w-4 shrink-0" /> : <MessageSquare className="mr-2 h-4 w-4 shrink-0" />}
        <div className="flex min-w-0 flex-col">
          <span className="truncate">
            {truncate(session.title || "Untitled", 50)}
          </span>
          {session.snippet && <span className="truncate type-body-xs-regular text-neutral-500 dark:text-neutral-400">
              {session.snippet}
            </span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        {session.managed_prs && session.managed_prs.length > 0 && <div className="flex items-center gap-1">
            {(() => {
          const counts = {
            open: 0,
            draft: 0,
            merged: 0,
            closed: 0
          };
          for (const pr of session.managed_prs) {
            counts[pr.status]++;
          }
          const badges: React.ReactNode[] = [];
          if (counts.open > 0) badges.push(<span key="open" className="inline-flex items-center gap-0.5 text-ds-green text-xs">
                    <GitPullRequest className="h-3 w-3" />
                    {counts.open}
                  </span>);
          if (counts.merged > 0) badges.push(<span key="merged" className="inline-flex items-center gap-0.5 text-ds-purple text-xs">
                    <GitMerge className="h-3 w-3" />
                    {counts.merged}
                  </span>);
          if (counts.draft > 0) badges.push(<span key="draft" className="inline-flex items-center gap-0.5 text-neutral-500 text-xs">
                    <GitPullRequestDraft className="h-3 w-3" />
                    {counts.draft}
                  </span>);
          if (counts.closed > 0) badges.push(<span key="closed" className="inline-flex items-center gap-0.5 text-ds-red text-xs">
                    <GitPullRequestClosed className="h-3 w-3" />
                    {counts.closed}
                  </span>);
          return badges;
        })()}
          </div>}
        <span className="text-xs text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
          {formatDistanceToNow(new Date(session.last_message_at || session.updated_at)).replace(/^(about|almost|over|less than)\s+/i, "")}
        </span>
      </div>
    </CommandItem>;
  const searchResults = useMemo(() => {
    // For 1 character, we still show "Search logs for" option
    if (!search || search.length === 0) return null;
    const lowerSearch = search.toLowerCase();
    const matchingWorkflows = search.length >= 2 ? workflows.filter(workflow => {
      const matches = workflow.toLowerCase().includes(lowerSearch);
      return matches;
    }) : [];
    const matchingJobs = search.length >= 2 ? jobs.filter(job => {
      const jobMatches = job.job_name.toLowerCase().includes(lowerSearch);
      const workflowMatches = job.workflow_name.toLowerCase().includes(lowerSearch);
      return jobMatches || workflowMatches;
    }) : [];
    return {
      workflowCount: matchingWorkflows.length,
      jobCount: matchingJobs.length,
      matchingWorkflows,
      matchingJobs,
      matchingSessions,
      hasSearch: true // Always true when we have any search text
    };
  }, [search, workflows, jobs, matchingSessions]);

  // Most recently active Codesmith sessions, surfaced at the top of the
  // palette when it opens on a Codesmith page with no search text.
  const recentSessions = useMemo(() => {
    if (!isOnSessionPage) return [];
    return getRecentSessions(cachedSessions, 3, {
      userId: user?.id
    });
  }, [isOnSessionPage, cachedSessions, user]);
  enum CommandSection {
    QUICK_ACTIONS = "Quick Actions",
    RECENT_SESSIONS = "Recent Sessions",
    MY_SESSIONS = "My Sessions",
    TEAM_SESSIONS = "Team Sessions",
    NAVIGATION = "Navigation",
    TIME_RANGE = "Time Range",
    CODESMITH_ACTIONS = "[code]smith Actions",
    ACTIONS = "Actions",
    HELP = "Help",
  }
  type NavigationItem = {
    name: string;
    icon: React.ReactElement;
    shortcut?: string;
    onSelect: () => void;
    priority?: number;
    /** Listed in the desktop app. */
    desktop?: true;
  };
  type NavigationGroup = {
    group: string;
    items: NavigationItem[];
    priority?: number;
  };
  type SearchResultItem = {
    key: string;
    element: React.ReactElement;
    priority: number;
    section: CommandSection;
  };
  const getNavigationItemPriority = (itemName: string): number => getNavigationItemPriorityFor(itemName, search);

  // Special priority function for "Switch organization" - requires 3 characters to match
  const getSwitchOrgPriority = (): number => {
    if (search.length >= 3 && "switch organization".toLowerCase().includes(search.toLowerCase())) {
      return 10; // Show it when user types 3+ characters that match
    }
    return -1; // Hide it by default
  };
  const getActionItemPriority = (itemName_0: string): number => getActionItemPriorityFor(itemName_0, search);
  const getTimePeriodPriority = (itemName_1: string): number => {
    // Only show time range options on specific pages
    const isOnRelevantPage = router.pathname.includes("/runs/workflows") || router.pathname.includes("/runs/jobs") || router.pathname.includes("/logs");
    if (!isOnRelevantPage) {
      return -1; // Hide on other pages
    }
    const basePriority = 1;
    const numberBoostPriority = 30;
    const searchNumber = parseInt(search, 10);
    if (!isNaN(searchNumber) && search.trim() === searchNumber.toString()) {
      if (itemName_1.toLowerCase().includes(search)) {
        return numberBoostPriority;
      }
      return basePriority;
    }
    if (search.length > 0 && itemName_1.toLowerCase().includes(search.toLowerCase())) {
      return numberBoostPriority;
    }
    return basePriority;
  };
  const navigationItems: NavigationGroup[] = [{
    group: "Navigation",
    items: [{
      name: "Jobs",
      icon: <CirclePlay className="mr-2 h-4 w-4" />,
      shortcut: "G J",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/runs/jobs` : "/runs/jobs"),
      get priority() {
        return getNavigationItemPriority("Jobs");
      }
    }, {
      name: "Workflows",
      icon: <Workflow className="mr-2 h-4 w-4" />,
      shortcut: "G W",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/runs/workflows` : "/runs/workflows"),
      get priority() {
        return getNavigationItemPriority("Workflows");
      }
    }, {
      name: "Logs",
      icon: <Terminal className="mr-2 h-4 w-4" />,
      shortcut: "G L",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/logs` : "/logs"),
      get priority() {
        return getNavigationItemPriority("Logs");
      }
    }, {
      name: "Actions Analytics",
      icon: <GaugeCircleIcon className="mr-2 h-4 w-4" />,
      shortcut: "G A",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/analytics` : "/analytics"),
      get priority() {
        return getNavigationItemPriority("Actions Analytics");
      }
    }, {
      name: "Docker Analytics",
      icon: <SiDocker className="mr-2 h-4 w-4" />,
      shortcut: "G D",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/docker-analytics` : "/docker-analytics"),
      get priority() {
        return getNavigationItemPriority("Docker Analytics");
      }
    }, {
      name: "Cache",
      icon: <DatabaseZap className="mr-2 h-4 w-4" />,
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/cache` : "/cache"),
      get priority() {
        return getNavigationItemPriority("Cache");
      }
    }, {
      name: "Sticky Disks",
      icon: <HardDrive className="mr-2 h-4 w-4" />,
      shortcut: "G S",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/stickydisks` : "/stickydisks"),
      get priority() {
        return getNavigationItemPriority("Sticky Disks");
      }
    }, {
      name: "Monitors",
      icon: <Activity className="mr-2 h-4 w-4" />,
      shortcut: "G M",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/monitors` : "/monitors"),
      get priority() {
        return getNavigationItemPriority("Monitors");
      }
    }, {
      name: "Rightsize Runners",
      icon: <Ruler className="mr-2 h-4 w-4" />,
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/rightsize` : "/rightsize"),
      get priority() {
        return getNavigationItemPriority("Rightsize Runners");
      }
    }, {
      name: "Machine Definitions",
      icon: <Layers className="mr-2 h-4 w-4" />,
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/machine-definitions` : "/machine-definitions"),
      get priority() {
        return getNavigationItemPriority("Machine Definitions");
      }
    },
    // Only surface Automations when the Codesmith automations feature is
    // enabled for the active org (mirrors the sidebar nav gating). Gated by
    // omission rather than a negative priority because nav items are added
    // with `item.priority || 0`, which would coerce -1 back to 0.
    ...(codesmithAutomationsEnabled ? [{
      name: "Automations",
      icon: <Zap className="mr-2 h-4 w-4" />,
      desktop: true as const,
      onSelect: () => navigate(resolvedOrg ? `/${resolvedOrg}/automations` : "/automations"),
      get priority() {
        return getNavigationItemPriority("Automations");
      }
    }] : []), ...(codesmithEnabled ? [{
      name: "[code]smith Usage",
      icon: <CodesmithIcon className="mr-2 size-4" />,
      onSelect: () => navigate(codesmithUsagePath(resolvedOrg)),
      get priority() {
        return getNavigationItemPriority("[code]smith Usage");
      }
    }] : []), {
      name: "GitHub Actions Usage",
      icon: <GithubActionsIcon className="mr-2 size-4" />,
      onSelect: () => navigate(githubActionsUsagePath(resolvedOrg)),
      get priority() {
        return getNavigationItemPriority("GitHub Actions Usage");
      }
    }, {
      name: "Usage & Billing",
      icon: <BarChart3 className="mr-2 h-4 w-4" />,
      shortcut: "G U",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/usage` : "/usage"),
      get priority() {
        return getNavigationItemPriority("Usage & Billing");
      }
    }, {
      name: "Settings",
      icon: <Settings className="mr-2 h-4 w-4" />,
      desktop: true,
      shortcut: isMac ? "⌘," : "Ctrl+,",
      // Mirrors the keyboard shortcut it advertises, including the
      // Codesmith tab targeting on Codesmith pages.
      onSelect: () => navigate(settingsPagePath(resolvedOrg, isOnCodesmithPage)),
      get priority() {
        return getNavigationItemPriority("Settings");
      }
    }, {
      name: "Switch organization",
      icon: <Building2 className="mr-2 h-4 w-4" />,
      desktop: true,
      onSelect: () => {
        window.dispatchEvent(new CustomEvent("openOrgSwitcher"));
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getSwitchOrgPriority();
      }
    }]
  }, ...(conversationAction ? [{
    group: "[code]smith Actions",
    items: [{
      name: conversationAction.action === "archive" ? "Archive conversation" : "Unarchive conversation",
      icon: conversationAction.action === "archive" ? <Archive className="mr-2 h-4 w-4" /> : <ArchiveRestore className="mr-2 h-4 w-4" />,
      desktop: true as const,
      onSelect: () => {
        window.dispatchEvent(new CustomEvent(CODESMITH_CONVERSATION_ACTION_EVENT, {
          detail: conversationAction
        }));
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getActionItemPriority(conversationAction.action === "archive" ? "Archive conversation" : "Unarchive conversation");
      }
    }]
  }] : []), {
    group: "Actions",
    items: [{
      name: "Open MCP settings",
      icon: <Settings className="mr-2 h-4 w-4" />,
      desktop: true,
      onSelect: () => {
        navigate(`${settingsPath(currentOrg, "codesmith", "mcp")}#mcp-servers`);
        // router.push uses history.pushState, which does NOT fire the
        // browser's hashchange event. If the user is already on the MCP
        // settings page, neither the pathname nor hashchange updates, so
        // the scroll-to-hash listener in the settings layout would never
        // run. Dispatch a synthetic hashchange so that listener handles
        // every entry path uniformly. Harmless when the layout hasn't
        // mounted yet (no listener to receive it).
        if (typeof window !== "undefined") {
          setTimeout(() => {
            window.dispatchEvent(new Event("hashchange"));
          }, 0);
        }
      },
      get priority() {
        return getActionItemPriority("Open MCP settings");
      }
    }, {
      name: "Copy prompt for AI",
      icon: <Sparkles className="mr-2 h-4 w-4" />,
      onSelect: () => {
        window.dispatchEvent(new CustomEvent("copyTestAIPrompt"));
        setOpen(false);
        setSearch("");
      },
      get priority() {
        const isTestsPage = router.query.tab === "tests" && router.pathname.includes("/jobs/");
        return isTestsPage ? getActionItemPriority("Copy prompt for AI") : -1;
      }
    }, {
      name: perfDebugActionName,
      icon: <Activity className="mr-2 h-4 w-4" />,
      desktop: true,
      onSelect: () => {
        setPerfDebugEnabled(!perfDebugEnabled);
        setOpen(false);
        setSearch("");
      },
      get priority() {
        if (!canUsePerfDebug(user)) return -1;
        // A debug tool: listed only when searched for, or while it is on.
        const priority = getActionItemPriority(perfDebugActionName);
        return priority > 1 || perfDebugEnabled ? priority : -1;
      }
    }, ...[{
      key: "light",
      name: "Change theme to light mode",
      icon: <Sun className="mr-2 h-4 w-4" />
    }, {
      key: "dark",
      name: "Change theme to dark mode",
      icon: <Moon className="mr-2 h-4 w-4" />
    }, {
      key: "system",
      name: "Change theme to system mode",
      icon: <Monitor className="mr-2 h-4 w-4" />
    }].filter(opt => opt.key !== theme).map(opt_0 => ({
      name: opt_0.name,
      icon: opt_0.icon,
      desktop: true as const,
      onSelect: () => {
        setTheme(opt_0.key);
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getActionItemPriority(opt_0.name);
      }
    }))]
  }, {
    group: "Time Range",
    items: [{
      name: "Last 1 hour",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges = labelsToDateRanges();
        setRecentTimeTab("1h");
        setRecentDateRange(dateRanges["1h"]);
        updateDateRangeUrl("1h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 1 hour");
      }
    }, {
      name: "Last 2 hours",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_0 = labelsToDateRanges();
        setRecentTimeTab("2h");
        setRecentDateRange(dateRanges_0["2h"]);
        updateDateRangeUrl("2h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 2 hours");
      }
    }, {
      name: "Last 4 hours",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_1 = labelsToDateRanges();
        setRecentTimeTab("4h");
        setRecentDateRange(dateRanges_1["4h"]);
        updateDateRangeUrl("4h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 4 hours");
      }
    }, {
      name: "Last 12 hours",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_2 = labelsToDateRanges();
        setRecentTimeTab("12h");
        setRecentDateRange(dateRanges_2["12h"]);
        updateDateRangeUrl("12h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 12 hours");
      }
    }, {
      name: "Last 1 day",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_3 = labelsToDateRanges();
        setRecentTimeTab("1D");
        setRecentDateRange(dateRanges_3["1D"]);
        updateDateRangeUrl("1D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 1 day");
      }
    }, {
      name: "Last 2 days",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_4 = labelsToDateRanges();
        setRecentTimeTab("2D");
        setRecentDateRange(dateRanges_4["2D"]);
        updateDateRangeUrl("2D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 2 days");
      }
    }, {
      name: "Last 5 days",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_5 = labelsToDateRanges();
        setRecentTimeTab("5D");
        setRecentDateRange(dateRanges_5["5D"]);
        updateDateRangeUrl("5D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 5 days");
      }
    }, {
      name: "Last 14 days",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_6 = labelsToDateRanges();
        setRecentTimeTab("14D");
        setRecentDateRange(dateRanges_6["14D"]);
        updateDateRangeUrl("14D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 14 days");
      }
    }]
  }, {
    group: "Help",
    items: [{
      name: "View documentation",
      icon: <FileText className="mr-2 h-4 w-4" />,
      desktop: true,
      onSelect: () => {
        window.open("https://docs.blacksmith.sh", "_blank");
        setOpen(false);
      },
      priority: 0
    }, {
      name: "Get support",
      icon: <HelpCircle className="mr-2 h-4 w-4" />,
      onSelect: () => {
        navigate(currentOrg ? `/${currentOrg}/support` : "/FastActions/support");
      },
      priority: 0
    }]
  }];
  return <DialogPrimitive.Root open={open} onOpenChange={newOpen => {
    if (newOpen === false && search) {
      setSearch("");
      return;
    }
    setOpen(newOpen);
  }} modal={true}>
      <CommandDialogContent onPointerDownOutside={e => {
      if (search) {
        e.preventDefault();
        setSearch("");
      }
    }} onInteractOutside={e_0 => {
      if (search) {
        e_0.preventDefault();
        setSearch("");
      }
    }} onEscapeKeyDown={e_1 => {
      e_1.preventDefault();
      e_1.stopPropagation();
      if (search) {
        setSearch("");
      } else {
        setOpen(false);
      }
    }}>
        <Command ref={commandRef} className="rounded-lg" shouldFilter={false} value={selectedValue} onValueChange={setSelectedValue} onKeyDown={e_2 => {
        const isVimNavigation = e_2.ctrlKey && ["n", "p", "j", "k"].includes(e_2.key);
        if (isVimNavigation || ["ArrowUp", "ArrowDown", "Home", "End"].includes(e_2.key)) {
          hasExplicitSelectionRef.current = true;
        }
      }} onPointerMove={e_3 => {
        if ((e_3.target as HTMLElement).closest("[cmdk-item]")) {
          hasExplicitSelectionRef.current = true;
        }
      }}>
          <CommandInput placeholder="Type a command or search..." value={search} onValueChange={value => {
          hasExplicitSelectionRef.current = false;
          setSearch(value);
        }} autoFocus />
          <CommandList>
            {!isSessionSearchLoading && <CommandEmpty>No results found.</CommandEmpty>}
            {(() => {
            const allItems: SearchResultItem[] = [];
            if (search && searchResults && !desktop) {
              // Workflow matches - priority 50, but 1 if more than 20 matches
              if (search.length >= 2 && searchResults.workflowCount > 0) {
                allItems.push({
                  key: "search-workflows",
                  priority: searchResults.workflowCount > 20 ? 1 : 50,
                  section: CommandSection.QUICK_ACTIONS,
                  element: <CommandItem onSelect={() => {
                    const params = new URLSearchParams();
                    searchResults.matchingWorkflows.forEach(workflow_0 => {
                      params.append("workflow", workflow_0);
                    });
                    const url_0 = currentOrg ? `/${currentOrg}/runs/workflows?${params.toString()}` : `/runs/workflows?${params.toString()}`;
                    navigate(url_0);
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <Workflow className="mr-2 h-4 w-4" />
                          <span>
                            View {searchResults.workflowCount} matching{" "}
                            {searchResults.workflowCount === 1 ? "workflow" : "workflows"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Show first match */}
                          <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingWorkflows[0]}>
                            {truncate(searchResults.matchingWorkflows[0], 20)}
                          </Badge>

                          {/* Show second match if exists */}
                          {searchResults.workflowCount > 1 && <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingWorkflows[1]}>
                              {truncate(searchResults.matchingWorkflows[1], 20)}
                            </Badge>}

                          {/* Show more badge with tooltip if > 2 matches */}
                          {searchResults.workflowCount > 2 && <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 cursor-help">
                                    {searchResults.workflowCount - 2} more...
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-md">
                                  <div className="flex flex-wrap gap-1">
                                    {searchResults.matchingWorkflows.slice(2).map(workflow_1 => <Badge key={workflow_1} variant="secondary" className="text-xs" title={workflow_1}>
                                          {truncate(workflow_1, 30)}
                                        </Badge>)}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>}
                        </div>
                      </CommandItem>
                });
              }

              // Job matches - priority 50, but 1 if more than 20 matches
              if (search.length >= 2 && searchResults.jobCount > 0) {
                allItems.push({
                  key: "search-jobs",
                  priority: searchResults.jobCount > 20 ? 1 : 50,
                  section: CommandSection.QUICK_ACTIONS,
                  element: <CommandItem onSelect={() => {
                    const jobExpressions = searchResults.matchingJobs.map(job_0 => `${job_0.workflow_name}::${job_0.job_name}`);
                    const orExpression = `(${jobExpressions.join(" OR ")})`;
                    const params_0 = new URLSearchParams();
                    params_0.set("job", orExpression);
                    const url_1 = currentOrg ? `/${currentOrg}/runs/jobs?${params_0.toString()}` : `/runs/jobs?${params_0.toString()}`;
                    navigate(url_1);
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <CirclePlay className="mr-2 h-4 w-4" />
                          <span>
                            View {searchResults.jobCount} matching{" "}
                            {searchResults.jobCount === 1 ? "job" : "jobs"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Show first match */}
                          <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingJobs[0].job_name}>
                            {truncate(searchResults.matchingJobs[0].job_name, 20)}
                          </Badge>

                          {/* Show second match if exists */}
                          {searchResults.jobCount > 1 && <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingJobs[1].job_name}>
                              {truncate(searchResults.matchingJobs[1].job_name, 20)}
                            </Badge>}

                          {/* Show more badge with tooltip if > 2 matches */}
                          {searchResults.jobCount > 2 && <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 cursor-help">
                                    {searchResults.jobCount - 2} more...
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-md">
                                  <div className="flex flex-wrap gap-1">
                                    {searchResults.matchingJobs.slice(2).map(job_1 => <Badge key={`${job_1.workflow_name}:${job_1.job_name}`} variant="secondary" className="text-xs" title={job_1.job_name}>
                                          {truncate(job_1.job_name, 30)}
                                        </Badge>)}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>}
                        </div>
                      </CommandItem>
                });
              }

              // Search logs - priority 40, but lower if workflows/jobs have poor matches
              const hasGoodWorkflowJobMatches = searchResults.workflowCount > 0 && searchResults.workflowCount <= 20 || searchResults.jobCount > 0 && searchResults.jobCount <= 20;
              allItems.push({
                key: "search-logs",
                priority: hasGoodWorkflowJobMatches ? 40 : 10,
                section: CommandSection.QUICK_ACTIONS,
                element: <CommandItem onSelect={() => {
                  // If search contains spaces, wrap in quotes for exact phrase search
                  const searchQuery = search.includes(" ") ? `"${search}"` : search;
                  const url_2 = currentOrg ? `/${currentOrg}/logs?q=${encodeURIComponent(searchQuery)}` : `/logs?q=${encodeURIComponent(searchQuery)}`;
                  navigate(url_2);
                }} className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center">
                        <Search className="mr-2 h-4 w-4" />
                        <span>Search logs for &ldquo;{search}&rdquo;</span>
                      </div>
                    </CommandItem>
              });
            }

            // Codesmith chat matches - split into My Sessions and Team Sessions
            if (search && searchResults && searchResults.matchingSessions.length > 0) {
              const mySessionPriority = isOnSessionPage ? 100 : 3;
              const teamSessionPriority = isOnSessionPage ? 99 : 2;
              searchResults.matchingSessions.forEach(session_0 => {
                const section = session_0.isMine ? CommandSection.MY_SESSIONS : CommandSection.TEAM_SESSIONS;
                const priority = session_0.isMine ? mySessionPriority : teamSessionPriority;
                allItems.push({
                  key: `session-${session_0.id}`,
                  priority,
                  section,
                  element: renderSessionItem(session_0)
                });
              });
            }

            // Holds the slot session matches will fill, just below any
            // retained matches, so arriving results don't push other
            // sections around. With only team matches showing, it sits at
            // the end of Team Sessions rather than opening an empty
            // My Sessions group above them.
            if (search && isSessionSearchLoading) {
              const showsOnlyTeamMatches = allItems.some(item_0 => item_0.section === CommandSection.TEAM_SESSIONS) && !allItems.some(item => item.section === CommandSection.MY_SESSIONS);
              const loadingPriority = showsOnlyTeamMatches ? isOnSessionPage ? 96.5 : 1.5 : isOnSessionPage ? 99.5 : 2.5;
              allItems.push({
                key: "session-search-loading",
                priority: loadingPriority,
                section: showsOnlyTeamMatches ? CommandSection.TEAM_SESSIONS : CommandSection.MY_SESSIONS,
                element: <div role="status" className="flex items-center gap-2 px-3 py-[3px] type-body-s-regular text-neutral-500 dark:text-neutral-400">
                      <LoadingSpinner className="size-4 shrink-0" />
                      Searching sessions...
                    </div>
              });
            }

            // Recent Codesmith sessions shown at the top when the palette
            // opens on a Codesmith page with no search text
            if (!search && recentSessions.length > 0) {
              recentSessions.forEach(session_1 => {
                allItems.push({
                  key: `recent-session-${session_1.id}`,
                  priority: 100,
                  section: CommandSection.RECENT_SESSIONS,
                  element: renderSessionItem(session_1)
                });
              });
            }

            // Dynamic time period options when typing a number
            const isOnRelevantPage_0 = router.pathname.includes("/runs/workflows") || router.pathname.includes("/runs/jobs") || router.pathname.includes("/logs");
            const searchNumber_0 = parseInt(search, 10);
            if (isOnRelevantPage_0 && !isNaN(searchNumber_0) && search.trim() === searchNumber_0.toString() && searchNumber_0 >= 1 && searchNumber_0 <= 32) {
              const dateRanges_7 = labelsToDateRanges();

              // Define preset values to avoid duplicates
              const presetHours = [1, 2, 4, 12];
              const presetDays = [1, 2, 5, 14];
              if (searchNumber_0 <= 24 && !presetHours.includes(searchNumber_0)) {
                const hourKey = `${searchNumber_0}h`;
                allItems.push({
                  key: `dynamic-hours-${searchNumber_0}`,
                  priority: 35,
                  section: CommandSection.TIME_RANGE,
                  element: <CommandItem onSelect={() => {
                    if (dateRanges_7[hourKey]) {
                      setRecentTimeTab(hourKey);
                      setRecentDateRange(dateRanges_7[hourKey]);
                      updateDateRangeUrl(hourKey);
                    } else {
                      const today = new Date();
                      const customRange = {
                        from: new Date(today.getTime() - searchNumber_0 * 60 * 60 * 1000),
                        to: today
                      };
                      setRecentTimeTab(hourKey);
                      setRecentDateRange(customRange);
                      updateDateRangeUrl(hourKey, customRange);
                    }
                    setOpen(false);
                    setSearch("");
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4" />
                          <span>
                            Last {searchNumber_0}{" "}
                            {searchNumber_0 === 1 ? "hour" : "hours"}
                          </span>
                        </div>
                      </CommandItem>
                });
              }
              if (!presetDays.includes(searchNumber_0)) {
                const dayKey = `${searchNumber_0}D`;
                allItems.push({
                  key: `dynamic-days-${searchNumber_0}`,
                  priority: 35,
                  section: CommandSection.TIME_RANGE,
                  element: <CommandItem onSelect={() => {
                    if (dateRanges_7[dayKey]) {
                      setRecentTimeTab(dayKey);
                      setRecentDateRange(dateRanges_7[dayKey]);
                      updateDateRangeUrl(dayKey);
                    } else {
                      const today_0 = new Date();
                      const customRange_0 = {
                        from: new Date(today_0.getTime() - searchNumber_0 * 24 * 60 * 60 * 1000),
                        to: today_0
                      };
                      setRecentTimeTab(dayKey);
                      setRecentDateRange(customRange_0);
                      updateDateRangeUrl(dayKey, customRange_0);
                    }
                    setOpen(false);
                    setSearch("");
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4" />
                          <span>
                            Last {searchNumber_0}{" "}
                            {searchNumber_0 === 1 ? "day" : "days"}
                          </span>
                        </div>
                      </CommandItem>
                });
              }
            }
            navigationItems.forEach(section_0 => {
              const surfaceItems = desktop ? section_0.items.filter(item_1 => item_1.desktop) : section_0.items;
              const filteredItems = search ? surfaceItems.filter(item_2 => navigationItemMatchesSearch(item_2.name, search)) : surfaceItems;
              filteredItems.forEach(item_3 => {
                const itemSection = section_0.group === "Help" ? CommandSection.HELP : section_0.group === "[code]smith Actions" ? CommandSection.CODESMITH_ACTIONS : section_0.group === "Actions" ? CommandSection.ACTIONS : section_0.group === "Time Range" ? CommandSection.TIME_RANGE : CommandSection.NAVIGATION;
                allItems.push({
                  key: `nav-${item_3.name}`,
                  priority: item_3.priority || 0,
                  section: itemSection,
                  element: <CommandItem key={item_3.name} onSelect={item_3.onSelect} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          {item_3.icon}
                          <span>{item_3.name}</span>
                        </div>
                        {item_3.shortcut && <CommandShortcut className="text-neutral-400 dark:text-neutral-500">
                            {item_3.shortcut}
                          </CommandShortcut>}
                      </CommandItem>
                });
              });
            });
            const relevantItems = allItems.filter(item_4 => item_4.priority >= 0);
            const groupedBySection = relevantItems.reduce((acc, item_5) => {
              if (!acc[item_5.section]) {
                acc[item_5.section] = [];
              }
              acc[item_5.section].push(item_5);
              return acc;
            }, {} as Record<CommandSection, SearchResultItem[]>);

            // Calculate section priority (max priority of items in section) and sort sections
            const sectionsWithPriority = Object.keys(groupedBySection).map(sectionKey => {
              const section_1 = sectionKey as CommandSection;
              const items = groupedBySection[section_1];
              const maxPriority = Math.max(...items.map(item_6 => item_6.priority));
              const sortedItems = items.sort((a, b) => b.priority - a.priority);
              return {
                section: section_1,
                maxPriority,
                items: sortedItems
              };
            }).filter(sectionData => sectionData.maxPriority >= 0) // Filter out sections with negative max priority
            .sort((a_0, b_0) => b_0.maxPriority - a_0.maxPriority); // Sort sections by max priority (descending)

            return sectionsWithPriority.map((sectionData_0, index) => {
              const isLastSection = index === sectionsWithPriority.length - 1;
              return <React.Fragment key={sectionData_0.section}>
                    <CommandGroup heading={sectionData_0.section}>
                      {sectionData_0.items.map(item_7 => <React.Fragment key={item_7.key}>
                          {item_7.element}
                        </React.Fragment>)}
                    </CommandGroup>
                    {!isLastSection && <CommandSeparator className="my-2" />}
                  </React.Fragment>;
            });
          })()}
          </CommandList>
          <div className="border-t border-[var(--color-border)] dark:border-[var(--color-border)] px-4 py-2 text-xs text-neutral-500 dark:text-neutral-400 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShortcutKeys keys={["↑", "↓"]} />
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-2">
              <ShortcutKeys keys={["⏎"]} />
              <span>Select</span>
            </div>
            <div className="flex items-center gap-2">
              <ShortcutKeys keys={["esc"]} />
              <span>{search ? "Clear" : "Close"}</span>
            </div>
          </div>
        </Command>
      </CommandDialogContent>
    </DialogPrimitive.Root>;
}